import React, { useState, useRef, useEffect, useContext, useImperativeHandle, forwardRef } from "react";
import { IconButton, LinearProgress, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import globalStyles from "../../../Styles/global.module.scss";
import { Accept, useDropzone } from "react-dropzone";
import { useSnackbar } from "../GlobalSnackbar/GlobalSnackbar";
import Icons from "../Icons/Icons";
import { DashboardContext, DashboardDetailPayload } from "../../../Context/DashboardDetailContext";

// Define the file type for accepted files
interface GlobalDropZoneProps {
  onFileSelect: (file: File | null) => void;
  disabled?: boolean;
  uploadProgress?: number | null;
}

// const GlobalDropZone = forwardRef(({
//   handleFileSelect, disabled, uploadProgress, selectedFile, allowedType, handleCancel
// }: any, ref) => {

const GlobalDropZone = forwardRef(({ handleFileSelect, disabled, uploadProgress, selectedFile, allowedType, handleCancel }: any, ref: any) => {
  const { showSnackbar } = useSnackbar()

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [icon, setIcon] = useState<React.ReactNode>();
  const [file, setFile] = useState<any | null>(null);

  const dropzoneRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const acceptedFileTypes: Accept =
    allowedType === "pdf"
      ? { "application/pdf": [".pdf"] }
      : { "image/png": [".png"], "image/jpeg": [".jpeg"], "image/jpg": [".jpg"] };
  const maxSize = allowedType === "pdf" ? 50 * 1024 * 1024 : 2 * 1024 * 1024;

  useImperativeHandle(ref, () => ({
    resetFileInput: () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reset the input value
      }
    }
  }));

  const handleCancelClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    handleCancel();
  };

  useEffect(() => {
    if (selectedFile) {
      setFile(selectedFile);
      if (allowedType === "pdf" || selectedFile.type === "application/pdf") {
        setIcon(<Icons iconName='pdf-file-icon' iconWidth="32px" iconHeight="32px" />);
      } else if (selectedFile.type === ".jpg") {
        setIcon(<Icons iconName='jpg-file-icon' iconWidth="32px" iconHeight="32px" />);
      } else if (selectedFile.type === ".png") {
        setIcon(<Icons iconName='png-file-icon' iconWidth="32px" iconHeight="32px" />);
      } else if (selectedFile.type === ".jpeg") {
        setIcon(<Icons iconName='jpeg-icon' iconWidth="32px" iconHeight="32px" />);
      }
    } else {
      setFile(null);
    }
  }, [selectedFile]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropzoneRef.current && !dropzoneRef.current.contains(event.target as Node)) {
        setErrorMessage(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatFileSize = (size: number) => {
    return `${Math.ceil(size / 1024)} KB`; // Convert bytes to KB and round up
  };

  const onDrop = (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;
    if (allowedType === "pdf") {
      setIcon(<Icons iconName='pdf-file-icon' iconWidth="32px" iconHeight="32px" />)
    } else if (selectedFile.type === "image/jpg") {
      setIcon(<Icons iconName='jpg-file-icon' iconWidth="32px" iconHeight="32px" />); //
    } else if (selectedFile.type === "image/png") {
      setIcon(<Icons iconName='png-file-icon' iconWidth="32px" iconHeight="32px" />);
    } else if (selectedFile.type === "image/jpeg") {
      setIcon(<Icons iconName='jpeg-file-icon' iconWidth="32px" iconHeight="32px" />);
    }

    // Validate file type
    const isPdf = selectedFile.type === "application/pdf";
    const isImage = ["image/png", "image/jpeg", "image/jpg"].includes(selectedFile.type);

    if (allowedType === "pdf" && !isPdf) {
      showSnackbar("Only PDF files are allowed.", "error");
      return;
    }
    if (allowedType === "image" && !isImage) {
      showSnackbar("Only image files (PNG, JPG, JPEG) are allowed.", "error");
      return;
    }

    // Validate file size
    if (selectedFile.size > maxSize) {
      setErrorMessage(allowedType === "pdf" ? "PDF should be less than 50 MB" : "Image should be less than 2 MB");
      return
    }

    handleFileSelect(selectedFile);
    setErrorMessage(null);
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
      fileInputRef.current.value = '';
    }
  };

  const handleBlur = () => {
    setErrorMessage(null);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileTypes as Accept,
    disabled,
  });

  return (
    <div onBlur={handleBlur} ref={dropzoneRef}>
      <Grid
        container
        {...getRootProps()}
        sx={{
          border: errorMessage ? "1px dashed #E5001C" : "1px dashed #FFFFFF",
          backgroundColor: errorMessage ? "#E5001C1A" : "#FFFFFF4D",
          borderRadius: "5px",
          textAlign: "center",
          padding: "20px ",
          width: "100%",
        }}
      >
        <input {...getInputProps()} ref={fileInputRef} style={{ display: "none" }} />
        {!file ? (
          <Grid size={{ xs: 12 }} >
            <Grid container sx={{ alignItems: "center", justifyContent: "center", columnGap: "10px", }}>
              <Grid>
                <CloudUploadOutlinedIcon
                  sx={{ fontSize: "30px", color: "white" }}
                />
              </Grid>
              <Grid>
                <Typography
                  component={"span"}
                  className={`${globalStyles.globalTextV15} ${globalStyles.gblCWhite}`}
                >
                  Drag & Drop an {allowedType == "image" ? "image" : allowedType == "pdf" ? "pdf" : ""} or{" "}
                  <span style={{ color: "#B1D8FF", cursor: "pointer" }} onClick={handleBrowseClick}>
                    Browse
                  </span>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid size={{ xs: 12 }}>
              <Grid container sx={{ alignItems: "center", justifyContent: "space-between", }}>
                <Grid size={{ xs: 1 }}>
                  {icon}
                </Grid>
                <Grid size={{ xs: 9.5 }}>
                  <Grid container textAlign="left">
                    <Grid size={{ xs: 12 }}>
                      <Typography
                        component={"span"}
                        className={`${globalStyles.globalTextV15} ${globalStyles.gblCWhite} ${globalStyles.gblOnelineEllipsis}`}
                      >
                        {file?.google_storage_original_file_name}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography
                        component={"span"}
                        className={`${globalStyles.globalTextV14} ${globalStyles.gblC7C7C7}`}
                      >
                        {formatFileSize(file?.google_storage_file_size)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 1 }}>
                  <IconButton onClick={handleCancelClick} color="error" disabled={disabled}>
                    <CancelOutlinedIcon sx={{ color: "#FFFFFF" }} />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
        {uploadProgress && (
          <Grid
            container
            sx={{
              alignItems: "center",
              width: "100%",
              columnGap: "10px",
            }}
          >
            <Grid size={{ xs: 12 }}>
              <LinearProgress />
            </Grid>
            {/* <Grid size={{ xs: 1 }}>
              <Typography
                component={"span"}
                className={`${globalStyles.globalTextV15} ${globalStyles.gblCWhite}`}
              >
                {uploadProgress}
              </Typography>
            </Grid> */}
          </Grid>
        )}
      </Grid>
      {errorMessage && (
        <Grid size={{ xs: 12 }} sx={{ marginTop: "10px" }} >
          <Typography
            component="span"
            className={`${globalStyles.globalTextV14} ${globalStyles.gblFF4949}`}
          >
            {errorMessage}
          </Typography>
        </Grid>
      )}
    </div>
  );
});

export default GlobalDropZone;